import React, { useEffect, useState } from "react";
import useApi from "../../hooks/useApi";
import { Search } from "lucide-react"; // Or your preferred icon pack

const LocationTransferGeneral = ({
  mode,
  WorkOrderData,
  setWorkOrderData,
  setCurrentLocation,
  drAccount,
  setDrAccount,
  drDesc,
  setDrDesc,
  crAccount,
  setCrAccount,
  crDesc,
  setCrDesc,
  BASE_URL,
  COMPANY_CODE,
  DEPARTMENT_NO,
  type = "SAMPLEISSUE",
}) => {
  const { request, loading } = useApi();
  const [salesmanList, setSalesmanList] = useState([]);
  const isSampleIssue = type === "SAMPLERETURN";

  const displayDrAccount = isSampleIssue ? crAccount : drAccount;
  const displayDrDesc = isSampleIssue ? crDesc : drDesc;
  const displayCrAccount = isSampleIssue ? drAccount : crAccount;
  const displayCrDesc = isSampleIssue ? drDesc : crDesc;

  const fetchList = async (url, setter) => {
    try {
      const response = await request({
        url,
        method: "GET",
        timeout: 10000,
      });
      setter(response || []);
    } catch (err) {
      console.error("Fetch error:", err);
      setter([]);
    }
  };

  useEffect(() => {
    fetchList(
      `${BASE_URL}/api/CpaysCount/${COMPANY_CODE}/LOCATONLIST_ALL/-/-`,
      setSalesmanList
    );
  }, []);

  useEffect(() => {
    if (mode === "edit") return;
    const defaultLocation = localStorage.getItem("departmentLocation") || "";
    if (defaultLocation) {
      setWorkOrderData((prev) => ({
        ...prev,
        salespersonId: defaultLocation,
      }));
      setCurrentLocation(defaultLocation);
    }
  }, []);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 p-4 text-sm mx-auto w-full bg-white rounded-lg border border-gray-100 shadow-sm items-start">

      {/* Left Section: Document Info & Date */}
      <div className="lg:col-span-4 col-span-1 flex items-center justify-between gap-4 h-full min-h-[38px]">
        <div className="flex items-center gap-2">
          <span className="text-gray-500 font-medium">Loc No:</span>
          <span className="text-red-600 font-bold text-base bg-red-50 px-2 py-0.5 rounded">
            {WorkOrderData?.documentNo || "-"}
          </span>
        </div>

        <div className="flex items-center gap-2">
          <label className="text-xs font-medium text-gray-600 whitespace-nowrap">Trfr Date:</label>
          <input
            type="date"
            value={WorkOrderData?.date || new Date().toISOString().split("T")[0]}
            onChange={(e) =>
              setWorkOrderData({
                ...WorkOrderData,
                date: e.target.value,
              })
            }
            className="border border-gray-300 px-3 py-1.5 rounded text-sm focus:ring-2 focus:ring-blue-400 focus:border-blue-400 focus:outline-none text-gray-700 transition-all"
          />
        </div>
      </div>

      {/* Middle Section: From & To Location Select Dropdowns */}
      <div className="lg:col-span-4 col-span-1">
        <div className="grid grid-cols-2 gap-3">
          {/* From Location Select */}
          <div className="relative">
            <select
              value={WorkOrderData?.salespersonId || ""}
              onChange={(e) => {
                const selectedLocation = e.target.value;
                setWorkOrderData({
                  ...WorkOrderData,
                  salespersonId: selectedLocation,
                  from_location_name:
                    salesmanList?.find((item) => item.loc_code === selectedLocation)?.DESC || "",
                });
                setCurrentLocation(selectedLocation);
              }}
              className="w-full bg-white border border-gray-300 text-gray-700 rounded px-3 py-1.5 text-sm appearance-none focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400 cursor-pointer transition-all pr-8"
            >
              <option value="" disabled hidden>From Location</option>
              {salesmanList?.map((person) => (
                <option key={person.loc_code} value={person.loc_code}>
                  {person.loc_code} - {person.DESC}
                </option>
              ))}
            </select>
            <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-400">
              <svg className="fill-current h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
                <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" />
              </svg>
            </div>
          </div>

          {/* To Location Select */}
          <div className="relative">
            <select
              value={WorkOrderData?.cordinatorid || ""}
              onChange={(e) =>
                setWorkOrderData({
                  ...WorkOrderData,
                  cordinatorid: e.target.value,
                  to_location_name: salesmanList?.filter(
                    (itemForFilter) => itemForFilter.loc_code === e.target.value
                  )[0]?.DESC || "",
                })
              }
              className="w-full bg-white border border-gray-300 text-gray-700 rounded px-3 py-1.5 text-sm appearance-none focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400 cursor-pointer transition-all pr-8"
            >
              <option value="" disabled hidden>To Location</option>
              {salesmanList?.map((coord) => (
                <option key={coord.loc_code} value={coord.loc_code}>
                  {coord.DESC}
                </option>
              ))}
            </select>
            <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-400">
              <svg className="fill-current h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
                <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" />
              </svg>
            </div>
          </div>
        </div>
      </div>

      {/* Right Section: Compact Inline Remarks Textarea */}
      <div className="lg:col-span-4 col-span-1 flex items-center gap-2 w-full">
        <textarea
          value={WorkOrderData?.remarks || ""}
          onChange={(e) =>
            setWorkOrderData({
              ...WorkOrderData,
              remarks: e.target.value,
            })
          }
          rows={1}
          className="w-full border border-gray-300 rounded px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400 text-gray-700 transition-all resize-none min-h-[38px] flex items-center"
          placeholder="Enter remarks..."
        />
      </div>

    </div>
  );
};

export default LocationTransferGeneral;